import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as pedidosApi from '../../api/pedidos';
import * as clientesApi from '../../api/clientes';
import * as estoqueApi from '../../api/estoque';
import { useExigeEmpresaEspecifica } from '../../hooks/useExigeEmpresaEspecifica';
import AvisoSelecioneEmpresa from '../../components/common/AvisoSelecioneEmpresa';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SearchableSelect from '../../components/common/SearchableSelect';
import { todayIso } from '../../utils/date';
import { getErrorMessage } from '../../utils/apiError';

export default function PedidoNovo() {
  const navigate = useNavigate();
  const { bloqueado, carregandoEmpresas, semEmpresaCadastrada } = useExigeEmpresaEspecifica();
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  const [clientes, setClientes] = useState([]);
  const [estoque, setEstoque] = useState([]);

  const [idCliente, setIdCliente] = useState('');
  const [dataPedido, setDataPedido] = useState(todayIso());
  const [observacoes, setObservacoes] = useState('');
  const [carrinho, setCarrinho] = useState([]);

  const [itemSelecionado, setItemSelecionado] = useState('');
  const [quantidade, setQuantidade] = useState('');

  useEffect(() => {
    if (bloqueado) {
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([clientesApi.listClientes(), estoqueApi.listEstoque()])
      .then(([c, e]) => {
        setClientes(c.data);
        setEstoque(e.data);
      })
      .catch(() => setErro('Erro ao carregar dados de apoio.'))
      .finally(() => setLoading(false));
  }, [bloqueado]);

  const opcoesEstoque = useMemo(
    () =>
      estoque
        .filter((i) => !carrinho.some((c) => c.id_item_lote === i.id_item_lote))
        .map((i) => ({
          value: i.id_item_lote,
          label: `${i.produto_nome} — ${i.qualidade_nome} (Lote ${i.lote_codigo}) — saldo ${Number(i.saldo_atual)} ${i.unidade_sigla}`,
        })),
    [estoque, carrinho]
  );

  const itemInfo = estoque.find((i) => i.id_item_lote === itemSelecionado);

  const adicionarAoCarrinho = () => {
    if (!itemInfo || !quantidade) return;
    const qtd = Number(quantidade);
    if (qtd <= 0 || qtd > Number(itemInfo.saldo_atual)) {
      setErro(`Quantidade inválida. Saldo disponível: ${Number(itemInfo.saldo_atual)} ${itemInfo.unidade_sigla}.`);
      return;
    }
    setErro('');
    setCarrinho((atual) => [
      ...atual,
      {
        id_item_lote: itemInfo.id_item_lote,
        quantidade: qtd,
        descricao: `${itemInfo.produto_nome} — ${itemInfo.qualidade_nome} (Lote ${itemInfo.lote_codigo})`,
        unidade: itemInfo.unidade_sigla,
      },
    ]);
    setItemSelecionado('');
    setQuantidade('');
  };

  const removerDoCarrinho = (id_item_lote) => setCarrinho((atual) => atual.filter((c) => c.id_item_lote !== id_item_lote));

  const salvar = async (confirmarNaHora) => {
    if (carrinho.length === 0) {
      setErro('Adicione ao menos um item ao pedido.');
      return;
    }
    setErro('');
    setSalvando(true);
    try {
      const { data } = await pedidosApi.createPedido({
        id_cliente: idCliente || null,
        data_pedido: dataPedido,
        observacoes: observacoes || null,
        itens: carrinho.map((c) => ({ id_item_lote: c.id_item_lote, quantidade: c.quantidade })),
      });
      if (confirmarNaHora) {
        await pedidosApi.confirmarPedido(data.id_pedido);
      }
      navigate(`/pedidos/${data.id_pedido}`);
    } catch (err) {
      setErro(getErrorMessage(err, 'Erro ao salvar pedido.'));
    } finally {
      setSalvando(false);
    }
  };

  if (carregandoEmpresas || loading) return <LoadingSpinner />;

  if (bloqueado) {
    return (
      <div>
        <PageHeader titulo="Novo pedido (Saída)" />
        <AvisoSelecioneEmpresa semEmpresaCadastrada={semEmpresaCadastrada} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader titulo="Novo pedido (Saída)" subtitulo="Vá adicionando os itens conforme separa os sacos" />

      {erro && (
        <div className="sf-login-alert mb-3">
          <i className="bi bi-exclamation-circle" />
          {erro}
        </div>
      )}

      <div className="sf-card p-4 mb-3">
        <div className="row g-3">
          <div className="col-md-6">
            <label className="sf-field-label">
              <span>Cliente <span className="fw-normal" style={{ color: 'var(--sf-text-muted)' }}>(opcional — deixe em branco para venda avulsa)</span></span>
              <SearchableSelect
                options={clientes.map((c) => ({ value: c.id_cliente, label: c.nome }))}
                value={idCliente}
                onChange={setIdCliente}
                placeholder="Venda avulsa (sem cliente)"
              />
            </label>
          </div>
          <div className="col-md-6">
            <label className="sf-field-label">
              <span>Data</span>
              <span className="sf-field">
                <i className="bi bi-calendar3" />
                <input type="date" required value={dataPedido} onChange={(e) => setDataPedido(e.target.value)} />
              </span>
            </label>
          </div>
        </div>
      </div>

      <div className="sf-card p-4 mb-3">
        <h2 className="h6 mb-3" style={{ fontWeight: 800 }}>Adicionar item</h2>
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-7">
            <label className="sf-field-label">
              <span>Produto / Qualidade / Lote</span>
              <SearchableSelect
                options={opcoesEstoque}
                value={itemSelecionado}
                onChange={setItemSelecionado}
                placeholder="Buscar item em estoque..."
              />
            </label>
          </div>
          <div className="col-6 col-md-3">
            <label className="sf-field-label">
              <span>Quantidade {itemInfo && `(${itemInfo.unidade_sigla})`}</span>
              <span className="sf-field">
                <input
                  type="number"
                  min="0.001"
                  step="0.001"
                  value={quantidade}
                  onChange={(e) => setQuantidade(e.target.value)}
                  disabled={!itemSelecionado}
                />
              </span>
            </label>
          </div>
          <div className="col-6 col-md-2">
            <button
              type="button"
              className="sf-btn sf-btn-primary w-100"
              onClick={adicionarAoCarrinho}
              disabled={!itemSelecionado || !quantidade}
              title="Adicionar item ao pedido"
              aria-label="Adicionar item ao pedido"
            >
              <i className="bi bi-plus-lg" />
            </button>
          </div>
        </div>
      </div>

      <div className="sf-card p-4 mb-3">
        <h2 className="h6 mb-3" style={{ fontWeight: 800 }}>Itens do pedido</h2>
        {carrinho.length === 0 && <p style={{ color: 'var(--sf-text-muted)' }} className="mb-0">Nenhum item adicionado ainda.</p>}
        {carrinho.map((c) => (
          <div key={c.id_item_lote} className="d-flex justify-content-between align-items-center gap-2 py-2" style={{ borderBottom: '1px solid #f4f3ed' }}>
            <div className="d-flex align-items-center gap-2">
              <span className="sf-cell-icon"><i className="bi bi-box-seam" /></span>
              <div>
                <div className="sf-cell-title">{c.descricao}</div>
                <div className="sf-cell-sub">{c.quantidade} {c.unidade}</div>
              </div>
            </div>
            <button
              type="button"
              className="sf-btn sf-btn-ghost"
              onClick={() => removerDoCarrinho(c.id_item_lote)}
              title={`Remover ${c.descricao} do pedido`}
              aria-label={`Remover ${c.descricao} do pedido`}
            >
              <i className="bi bi-trash" />
            </button>
          </div>
        ))}

        <div className="mt-3">
          <label className="sf-field-label">
            <span>Observações</span>
            <textarea className="form-control" rows={2} value={observacoes} onChange={(e) => setObservacoes(e.target.value)} />
          </label>
        </div>
      </div>

      <div className="d-flex flex-column flex-sm-row gap-2 mb-4">
        <button type="button" className="sf-btn sf-btn-primary flex-fill" style={{ height: 52 }} disabled={salvando} onClick={() => salvar(true)}>
          <i className="bi bi-check-circle" />
          Confirmar pedido
        </button>
        <button type="button" className="sf-btn sf-btn-ghost flex-fill" style={{ height: 52 }} disabled={salvando} onClick={() => salvar(false)}>
          Salvar como rascunho
        </button>
      </div>
    </div>
  );
}
